// ============================================================================
// Module: projectRules
//
// Data access + pure helpers for per-event project attribution rules (Phase
// 1.1 of docs/TOKEN_COST_ACCURACY_PLAN.md). Backend:
// supabase/migrations/20261002103746_project_attribution_rules.sql.
//
// Attribution is resolved at READ time, per usage event, in this order:
//   1. a `.syncsimple.json` hint the hook sends ({"project": "<name or id>"});
//   2. the first matching project_rules row (ascending priority, then
//      created_at);
//   3. the agent's default project (agents.project_id);
//   4. Unassigned.
// So editing a rule moves historical usage too -- there is no backfill step.
//
// RLS: admins of the team can read/write project_rules, managers can read,
// members see nothing. get_team_unassigned_usage is SECURITY DEFINER and
// scopes itself (admins: whole team, managers: their pods' agents, others:
// empty).
// ============================================================================

import { supabase } from './supabase'

export type RuleMatchType = 'repo' | 'folder'

export interface ProjectRule {
  id: string
  teamId: string
  priority: number
  matchType: RuleMatchType
  pattern: string
  projectId: string
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface ProjectRuleInput {
  matchType: RuleMatchType
  pattern: string
  projectId: string
}

export interface UnassignedUsageRow {
  gitRepo: string | null
  cwdRel: string | null
  eventCount: number
  totalTokens: number
  /** null when nothing in the group could be priced. */
  costUsd: number | null
  lastSeen: string | null
}

export const PATTERN_MAX_LENGTH = 300
export const PRIORITY_STEP = 10

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested in projectRules.test.ts)
// ---------------------------------------------------------------------------

const toNum = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

const toNullableNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export function normalizeRule(raw: Record<string, unknown>): ProjectRule {
  return {
    id: String(raw.id),
    teamId: String(raw.team_id),
    priority: toNum(raw.priority),
    matchType: raw.match_type === 'folder' ? 'folder' : 'repo',
    pattern: String(raw.pattern ?? ''),
    projectId: String(raw.project_id),
    createdBy: raw.created_by ? String(raw.created_by) : null,
    createdAt: String(raw.created_at ?? ''),
    updatedAt: String(raw.updated_at ?? ''),
  }
}

export function normalizeUnassignedRow(raw: Record<string, unknown>): UnassignedUsageRow {
  return {
    gitRepo: raw.git_repo ? String(raw.git_repo) : null,
    cwdRel: raw.cwd_rel ? String(raw.cwd_rel) : null,
    eventCount: toNum(raw.event_count),
    totalTokens: toNum(raw.total_tokens),
    costUsd: toNullableNum(raw.cost_usd),
    lastSeen: raw.last_seen ? String(raw.last_seen) : null,
  }
}

/** Evaluation order: priority ascending, ties broken by created_at (same as the server). */
export function sortRules<T extends Pick<ProjectRule, 'priority' | 'createdAt'>>(rules: T[]): T[] {
  return [...rules].sort((a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt))
}

/** Priority for a rule appended to the end of the list. */
export function nextPriority(rules: Pick<ProjectRule, 'priority'>[]): number {
  if (rules.length === 0) return PRIORITY_STEP
  const max = Math.max(...rules.map((r) => r.priority))
  return (Math.floor(max / PRIORITY_STEP) + 1) * PRIORITY_STEP
}

/** Returns a copy of `rules` with the rule `id` moved one place up or down. No-op at the edges. */
export function moveRule<T extends { id: string }>(rules: T[], id: string, direction: 'up' | 'down'): T[] {
  const idx = rules.findIndex((r) => r.id === id)
  const target = direction === 'up' ? idx - 1 : idx + 1
  if (idx < 0 || target < 0 || target >= rules.length) return [...rules]
  const next = [...rules]
  ;[next[idx], next[target]] = [next[target], next[idx]]
  return next
}

/**
 * Renumbers an already-ordered list to 10, 20, 30… and returns only the rows
 * whose priority actually changes, so a save issues the minimum of updates.
 */
export function renumberPriorities(
  orderedRules: Pick<ProjectRule, 'id' | 'priority'>[]
): { id: string; priority: number }[] {
  const changes: { id: string; priority: number }[] = []
  orderedRules.forEach((rule, i) => {
    const priority = (i + 1) * PRIORITY_STEP
    if (rule.priority !== priority) changes.push({ id: rule.id, priority })
  })
  return changes
}

/** Returns an error message, or null when the pattern is acceptable. */
export function validatePattern(pattern: string): string | null {
  const trimmed = pattern.trim()
  if (!trimmed) return 'Enter a pattern.'
  if (trimmed.length > PATTERN_MAX_LENGTH) return `Pattern must be ${PATTERN_MAX_LENGTH} characters or fewer.`
  return null
}

export interface RulePrefill {
  matchType: RuleMatchType
  pattern: string
  /**
   * A broader alternative to offer next to the prefilled pattern (folder rows:
   * `<folder>/**`, which also matches the folder itself). null when none.
   */
  suggestedPattern: string | null
}

/**
 * Prefill for "Create rule" from an Unassigned row: a repo row becomes a repo
 * rule for that exact slug; a folder-only row becomes a folder rule for that
 * folder, with `/**` offered as a suggestion. null when the row has neither
 * (nothing to match on).
 */
export function prefillFromUnassignedRow(row: Pick<UnassignedUsageRow, 'gitRepo' | 'cwdRel'>): RulePrefill | null {
  const repo = row.gitRepo?.trim()
  if (repo) return { matchType: 'repo', pattern: repo, suggestedPattern: null }

  const folder = row.cwdRel?.trim().replace(/\/+$/, '')
  if (!folder) return null
  if (folder.endsWith('/**') || folder === '**') return { matchType: 'folder', pattern: folder, suggestedPattern: null }
  return { matchType: 'folder', pattern: folder, suggestedPattern: `${folder}/**` }
}

/** Turns a PostgREST / Postgres error into a sentence an admin can act on. */
export function friendlyRuleError(err: unknown): string {
  const e = (err ?? {}) as { code?: string; message?: string; details?: string }
  const text = `${e.message ?? ''} ${e.details ?? ''}`.toLowerCase()
  if (e.code === '23514') {
    if (text.includes('pattern')) return `Pattern must be 1–${PATTERN_MAX_LENGTH} characters.`
    if (text.includes('match_type')) return 'Choose "Repository" or "Folder" as the match type.'
    return 'That project no longer exists in this team. Refresh and pick another project.'
  }
  if (e.code === '23503') return 'That project no longer exists. Refresh and pick another project.'
  if (e.code === '42501' || text.includes('row-level security')) return 'Only team admins can change attribution rules.'
  if (e.code === 'PGRST116') return 'That rule no longer exists. Refresh to see the current list.'
  return e.message || 'Something went wrong saving the rule.'
}

/** Sums the unassigned rows into one figure. costUsd stays null when no row was priced. */
export function sumUnassigned(rows: UnassignedUsageRow[]): {
  totalTokens: number
  costUsd: number | null
  eventCount: number
} {
  let totalTokens = 0
  let eventCount = 0
  let costUsd: number | null = null
  for (const r of rows) {
    totalTokens += r.totalTokens
    eventCount += r.eventCount
    if (r.costUsd !== null) costUsd = (costUsd ?? 0) + r.costUsd
  }
  return { totalTokens, costUsd, eventCount }
}

// ---------------------------------------------------------------------------
// Data access. All throw the raw Supabase error; callers map it with
// friendlyRuleError(). Casts: src/types/supabase.ts predates these objects.
// ---------------------------------------------------------------------------

const RULE_COLUMNS = 'id, team_id, priority, match_type, pattern, project_id, created_by, created_at, updated_at'

export async function listProjectRules(teamId: string): Promise<ProjectRule[]> {
  const { data, error } = await (supabase.from as any)('project_rules')
    .select(RULE_COLUMNS)
    .eq('team_id', teamId)
    .order('priority', { ascending: true })
    .order('created_at', { ascending: true })
  if (error) throw error
  return sortRules(((data ?? []) as Record<string, unknown>[]).map(normalizeRule))
}

export async function createProjectRule(
  teamId: string,
  input: ProjectRuleInput,
  priority: number
): Promise<ProjectRule> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  const { data, error } = await (supabase.from as any)('project_rules')
    .insert({
      team_id: teamId,
      priority,
      match_type: input.matchType,
      pattern: input.pattern.trim(),
      project_id: input.projectId,
      created_by: user?.id ?? null,
    })
    .select(RULE_COLUMNS)
    .single()
  if (error) throw error
  return normalizeRule(data as Record<string, unknown>)
}

export async function updateProjectRule(id: string, input: ProjectRuleInput): Promise<ProjectRule> {
  const { data, error } = await (supabase.from as any)('project_rules')
    .update({ match_type: input.matchType, pattern: input.pattern.trim(), project_id: input.projectId })
    .eq('id', id)
    .select(RULE_COLUMNS)
    .single()
  if (error) throw error
  return normalizeRule(data as Record<string, unknown>)
}

export async function deleteProjectRule(id: string): Promise<void> {
  const { error } = await (supabase.from as any)('project_rules').delete().eq('id', id)
  if (error) throw error
}

/**
 * Persists a new order: renumbers to 10, 20, 30… and updates only the rows
 * whose priority changed. There is no UNIQUE on priority, so the updates can
 * run in parallel without transient collisions.
 */
export async function reorderProjectRules(orderedRules: Pick<ProjectRule, 'id' | 'priority'>[]): Promise<void> {
  const changes = renumberPriorities(orderedRules)
  const results = await Promise.all(
    changes.map(({ id, priority }) =>
      (supabase.from as any)('project_rules').update({ priority }).eq('id', id)
    )
  )
  const failed = results.find((r: { error: unknown }) => r.error)
  if (failed) throw failed.error
}

const PAGE_SIZE = 1000
const MAX_PAGES = 10

/** Unassigned usage grouped by (git_repo, cwd_rel), tokens desc. Dates are team-local YYYY-MM-DD, inclusive. */
export async function fetchUnassignedUsage(teamId: string, from: string, to: string): Promise<UnassignedUsageRow[]> {
  const rows: UnassignedUsageRow[] = []
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const { data, error } = await (supabase.rpc as any)('get_team_unassigned_usage', {
      p_team_id: teamId,
      p_from: from,
      p_to: to,
    })
      .order('total_tokens', { ascending: false, nullsFirst: false })
      .order('git_repo', { ascending: true, nullsFirst: true })
      .order('cwd_rel', { ascending: true, nullsFirst: true })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1)
    if (error) throw error
    const batch = ((data ?? []) as Record<string, unknown>[]).map(normalizeUnassignedRow)
    rows.push(...batch)
    if (batch.length < PAGE_SIZE) break
  }
  return rows
}
